import React from 'react';
import TableHeader from 'components/table/TableHeader';
import TableRow from 'components/table/TableRow';
import TableCell from 'components/table/TableCell';
import Table from 'components/table/Table';
import {useDispatch, useSelector} from 'react-redux';
import {AppDispatch, RootState} from '../../../store/store';
import {useDropdownActionMenu} from '../../../hooks/useDropdownActionMenu';
import usePermissions from '../../../hooks/usePermissions';
import {SearchType} from '../../../types/search';
import {setIsEditing} from '../../client/clientVisit/clientVisitSlice';
import {setActiveTab} from './visitDocumentSlice';
import BlueButton from '../../../components/BlueButton';
import PlusIcon from '../../../assets/images/icons/PlusIcon';
import {VisitDocumentThunks} from './visitDocumentThunks';
import {VisitDocument} from '../../../types/visitDocument';
import StatusBadge from '../../../components/StatusBadge';
import {useNavigate} from 'react-router-dom';
import DotsMenu from '../../../components/DotsMenu';

const VisitDocumentsTable: React.FC<{openDrawer: () => void; visitId?: string}> = ({openDrawer, visitId}) => {
  const visitDocuments = useSelector(
    (state: RootState) => state.visitDocument.visitDocuments,
  );
  const dispatch = useDispatch<AppDispatch>();
  const pagy = useSelector((state: RootState) => state.visitDocument.pagy);
  const activeTab = useSelector((state: RootState) => state.visitDocument.activeTab);
  const {setDotsMenuIsOpen} = useDropdownActionMenu();
  const {checkPermission} = usePermissions();
  const loading = useSelector(
    (state: RootState) => state.visitDocument.status === 'loading',
  );
  const navigate = useNavigate();

  interface VisibleColumnsType {
    id: boolean;
    documentTemplate: boolean;
    status: boolean;
    createdAt: boolean;
    actions: boolean;
  }

  const [visibleColumns, setVisibleColumns] = React.useState<VisibleColumnsType>({
    id: true,
    documentTemplate: true,
    status: true,
    createdAt: true,
    actions: true,
  });

  const columnOptions = [
    {value: 'id', label: 'ID', isVisible: visibleColumns.id},
    {value: 'documentTemplate', label: 'Document Template', isVisible: visibleColumns.documentTemplate},
    {value: 'status', label: 'Status', isVisible: visibleColumns.status},
    {value: 'createdAt', label: 'Created At', isVisible: visibleColumns.createdAt},
    {value: 'actions', label: 'Actions', isVisible: visibleColumns.actions},
  ];

  const searchByOptions = [
    {
      label: 'Document Template',
      value: 'document_template_name',
      operator: 'like',
    },
    {
      label: 'Status',
      value: 'status',
      operator: '==',
    },
  ];

  const fetchData = async ({
    page,
    search,
    sortField,
    sortDirection,
    rowsPerPage,
  }: {
    page: number;
    search: SearchType<{[key: string]: {operator: string; value: string}}>;
    sortField: string;
    sortDirection: string;
    rowsPerPage: number;
  }): Promise<void> => {
    // Add visitId filter if provided
    if (visitId) {
      search.where = {
        ...search.where,
        client_visit_id: {
          operator: '==',
          value: visitId,
        },
      };
    }

    await dispatch(
      VisitDocumentThunks.index({
        page,
        search,
        sortField,
        sortDirection,
        rowsPerPage,
      }),
    );
  };

  const handleColumnToggle = (column: string) => {
    if (column in visibleColumns) {
      setVisibleColumns(prev => ({
        ...prev,
        [column]: !prev[column as keyof VisibleColumnsType],
      }));
    }
  };

  const handleOpenDrawer = (id: string) => async () => {
    dispatch(setIsEditing(false));
    openDrawer();
    await dispatch(VisitDocumentThunks.show(id));
  };

  const handleFillDocument = (visitDocument: VisitDocument) => {
    // Get the document template solution associated with this visit document
    const documentTemplateSolution = visitDocument.documentTemplateSolution;
    if (documentTemplateSolution?.id) {
      navigate(`/document-fill/${documentTemplateSolution.id}`);
    } else {
      console.error('No document template solution found for this visit document');
    }
  };

  return (
    <Table
      fetchData={fetchData}
      pagy={pagy}
      activeTab={activeTab}
      setActiveTab={(tab: string) => dispatch(setActiveTab(tab))}
      tabs={['Documents', 'Notes']}
      pageable={true}
      recordsName={'Documents'}
      searchable={true}
      columns={true}
      loading={loading}
      columnOptions={columnOptions}
      onColumnToggle={handleColumnToggle}
      searchByOptions={searchByOptions}
      blueButton={
        checkPermission('create', 'VisitDocument') ? (
          <BlueButton
            onClick={handleOpenDrawer('')}
            label=""
            icon={<PlusIcon />}
          />
        ) : null
      }>
            <thead>
              <tr>
                {visibleColumns.id && <TableHeader label="ID" sortBy="id" />}
                {visibleColumns.documentTemplate && <TableHeader label="Document Template" sortBy="documentTemplate" />}
                {visibleColumns.status && <TableHeader label="Status" sortBy="status" />}
                {visibleColumns.createdAt && <TableHeader label="Created At" sortBy="createdAt" />}
                {visibleColumns.actions && <TableHeader label="Actions" />}
              </tr>
            </thead>
      <tbody className="divide-y divide-gray-200 dark:divide-neutral-700">
        {visitDocuments.map((visitDocument: VisitDocument) => (
          <TableRow key={visitDocument.id}>
            {visibleColumns.id && (
              <TableCell>
                <div className="relative group">
                  <div className="w-full flex items-center gap-x-3">
                    <div className="flex-grow">
                      <span className="text-sm font-medium text-gray-700 dark:text-neutral-300">
                        {visitDocument.id}
                      </span>
                    </div>
                  </div>
                </div>
              </TableCell>
            )}
            {visibleColumns.documentTemplate && (
              <TableCell>
                {visitDocument.documentTemplate?.name || '-'}
              </TableCell>
            )}
            {visibleColumns.status && (
              <TableCell>
                <StatusBadge status={visitDocument.status ?? 'Unknown'} />
              </TableCell>
            )}
            {visibleColumns.createdAt && (
              <TableCell>
                {visitDocument.createdAt || '-'}
              </TableCell>
            )}
            {visibleColumns.actions && (
              <TableCell>
                <DotsMenu options={[
                  {
                    label: 'Fill Document',
                    onClick: () => handleFillDocument(visitDocument),
                    hoverClass: 'hover:bg-cyan-100',
                  },
                  // {
                  //   label: 'Edit',
                  //   onClick: () => handleOpenDrawer(visitDocument.id)(),
                  //   hoverClass: 'hover:bg-cyan-100',
                  // },
                ]} />
              </TableCell>
            )}
          </TableRow>
        ))}
      </tbody>
    </Table>
  );
};

export default VisitDocumentsTable;
